<template>
  <div class="mb-1 xl:mb-2">
    <div class="flex flex-row justify-between align-middle mb-[0.15rem] lg:mb-1">
      <label :for="fieldId" class="block text-xs lg:text-sm" :class="disabled ? 'text-grey-300' : 'text-grey-500'">
        {{ label }}<span v-if="required" class="text-red-500 ml-1 xl:ml-2">*</span>
      </label>
      <p v-if="strengthMessage && is_password" :class="strengthMessageClass">{{ strengthMessage }}</p>
      <p v-if="matchMessage && is_con_password" :class="matchMessageClass">{{ matchMessage }}</p>
    </div>

    <div class="relative flex text-xs lg:text-sm text-left">
      <span
        v-if="is_phone"
        class="border border-gray-300 border-r-0 rounded-l-[0.35rem] lg:rounded-l-[0.5rem] p-2 lg:border-2 xl:p-3 text-left w-auto"
        :class="{'bg-gray-100 text-gray-400': disabled}"
      >
        +63
      </span>
      <input :id="fieldId" @blur="handleBlur" :type="isVisible ? 'text' : (is_password || is_con_password || is_pin) ? 'password' : 'text'"
        v-model="inputValue" @input="handleInput" :placeholder="placeholder" autocomplete="off"
        class="border border-gray-300 rounded-[0.35rem] lg:rounded-[0.5rem] p-2 lg:border-2 xl:p-3 w-full text-left focus:outline-none  focus:ring-theme-primary focus:border-theme-primary" :style="{ textTransform: getTransformStyle() }"
        :class="{ 'md:rounded-l-none xl:rounded-l-none': is_phone }" :maxlength="getMaxLength()" :disabled="disabled"/>
      <span
        v-if="is_password || is_con_password || is_pin"
        @click="!disabled && toggleVisibility()"
        class="absolute flex items-center cursor-pointer eye-icon"
        :class="{ 'opacity-50 cursor-not-allowed': disabled }"
        style="top: 50%; transform: translateY(-50%); right: 3rem;"
      >
        <img :src="isVisible ? '@/assets/images/papsi_password_visible.svg' : '@/assets/images/papsi_password_invisible.svg'" alt="Toggle Visibility"
         style="width: 4rem;" />

      </span>
    </div>
    <p v-if="validationMessage" class="text-[0.55rem] lg:text-xs text-red-500 mt-1 xl:mt-3">{{ validationMessage }}</p>
  </div>
</template>

<script>

import { passwordStrength } from 'check-password-strength';

export default {
  name: "v-text",
  props: {
    label: String,
    fieldId: {
      type: String,
      required: true,
    },
    placeholder: String,
    is_password: Boolean,
    is_con_password: Boolean,
    is_username: Boolean,
    is_email: Boolean,
    is_accountnum: Boolean,
    is_serialnum: Boolean,
    is_phone: Boolean,
    is_pin: Boolean,
    is_firstname: Boolean,
    is_lastname: Boolean,
    is_name: Boolean,
    is_num: Boolean,
    is_zip: Boolean,
    is_money: Boolean,
    passwordValue: String,
    required: {
      type: Boolean,
      default: false,
    },
    modelValue: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
    },
  },
  data() {
    return {
      inputValue: this.modelValue || "",
      isVisible: false,
      validationMessage: "",
      strengthMessage: "",
      strengthMessageClass: "",
      matchMessage: "",
      matchMessageClass: "",
    };
  },
  watch: {
      modelValue(newValue) {
        if (!this.is_money) {
            this.inputValue = newValue;
            return;
        }

        // Don't overwrite while the formatted value is already displayed
        if (document.activeElement !== this.$el.querySelector("input")) {
            const num = parseFloat(newValue);
            if (!isNaN(num)) {
                this.inputValue = num.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        }
    },
    inputValue() {
      this.validateField();
    },
    passwordValue() {  // <-- Watch the password field for changes
      if (this.is_con_password) {
        this.validateField();
      }
    },
  },
  methods: {
    toggleVisibility() {
      this.isVisible = !this.isVisible;
    },
    handleBlur() {
        if (!this.is_money) return;

        const clean = this.inputValue.replace(/,/g, "");

        if (!clean) return;

        const num = parseFloat(clean);

        if (isNaN(num)) return;

        this.inputValue = num.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        // emit string without commas
        this.$emit("update:modelValue", num.toFixed(2));
    },
    handleInput() {
      if (this.disabled) return;

      if (!this.is_email) {
        this.inputValue = this.inputValue.toUpperCase();
      }

      if (this.is_phone) {
        // Remove non-numeric characters for phone
        this.inputValue = this.inputValue.replace(/\D/g, "");
      } 
      else if (this.is_firstname || this.is_lastname || this.is_name || this.is_accountnum || this.is_serialnum) {
        // Convert letters to uppercase
        this.inputValue = this.inputValue.toUpperCase();
      } 
      else if (this.is_num) {
        // Remove non-numeric characters
        this.inputValue = this.inputValue.replace(/\D/g, "");
      } 
      else if (this.is_money) {
        // Remove everything except digits and dot
        let raw = this.inputValue.replace(/[^0-9.]/g, "");

        // Prevent multiple dots
        const parts = raw.split(".");
        if (parts.length > 2) {
          raw = parts[0] + "." + parts.slice(1).join("");
        }

        // Split integer and decimal parts
        let [intPart, decPart] = raw.split(".");

        // Format integer with commas
        intPart = intPart ? Number(intPart).toLocaleString() : "";

        // Limit decimals to 2
        if (decPart !== undefined) {
          decPart = decPart.substring(0, 2);
          this.inputValue = `${intPart}.${decPart}`;
        } else {
          this.inputValue = intPart;
        }
      }

      // Validate the field after input
      this.validateField();

      // Emit modelValue
      if (this.is_money) {
        // Emit clean numeric string (without commas)
        const cleanValue = this.inputValue.replace(/,/g, "");
        this.$emit("update:modelValue", cleanValue);
      } else {
        let finalValue = this.inputValue;

        if (this.is_firstname || this.is_lastname || this.is_name || this.is_accountnum || this.is_serialnum) {
          finalValue = finalValue.toUpperCase();
        }

        this.$emit("update:modelValue", finalValue);
      }
    },
    validateField() {
      if (this.disabled) {
        this.validationMessage = "";
        this.$emit("validation-status", this.fieldId, true);
        return;
      }

      this.validationMessage = "";
      this.strengthMessage = "";
      this.strengthMessageClass = "";
      this.matchMessage = "";
      this.matchMessageClass = "";
      const value = this.inputValue;
      let isValid = true; // Track validity

      if (this.required && !value) {
        this.validationMessage = "This field is required.";
        isValid = false;
      }

      if (this.is_password) {
        const strength = this.checkPasswordStrength(value);
        this.strengthMessage = strength.message;
        this.strengthMessageClass = strength.class;

        if (!/^.*(?=.{8,})(?=.*\d)(?=.*[a-zA-Z])(?=.*[!@#$%^&*]).*$/.test(value)) {
          this.validationMessage = "Password must be at least 8 characters with letters, numbers, and special characters.";
          isValid = false;
        }

        if (strength.message === "Too weak" || strength.message === "Weak") {
          isValid = false;
        }
      }

      else if (this.is_con_password) {
        if (value === "") {
          this.validationMessage = "Confirm Password cannot be empty.";
          isValid = false;
        } else if (value !== this.passwordValue) {  // <-- Compare with the prop
          this.matchMessage = "Mismatched";
          this.matchMessageClass = "text-red";
          isValid = false;
        } else {
          this.matchMessage = "Matched";
          this.matchMessageClass = "text-green";
        }
      }

      else if (this.is_username) {
        if (!/^[a-zA-Z0-9]{6,}$/.test(value)) {
          this.validationMessage = "Username must be at least 6 characters without spaces or special characters.";
          isValid = false;
        }
      }

      else if (this.is_email) {
        if (!/^\S+@\S+\.\S+$/.test(value)) {
          this.validationMessage = "Enter a valid email address.";
          isValid = false;
        }
      }

      else if (this.is_phone) {
          if (!/^\d{10}$/.test(value)) {
              this.validationMessage = "Phone number must be exactly 10 digits.";
              isValid = false;
          } else if (!/^9\d{9}$/.test(value)) {
              this.validationMessage = "Phone number must start with 9.";
              isValid = false;
          }
      }

      // Emit validation status to parent
      this.$emit("validation-status", this.fieldId, isValid);
    },
    checkPasswordStrength(password) {
      const strength = passwordStrength(password).value;
      let strengthClass = "";
      switch (strength) {
        case "Too weak":
        case "Weak":
          strengthClass = "text-red";
          break;
        case "Medium":
          strengthClass = "text-yellow";
          break;
        case "Strong":
          strengthClass = "text-green";
          break;
      }
      return { message: strength.charAt(0).toUpperCase() + strength.slice(1), class: strengthClass }; // Capitalize the first letter
    },
    getTransformStyle() {
      return this.is_email ? "none" : "uppercase";
    },
    getMaxLength() {
      if (this.maxLength !== null) {
        return this.maxLength;
      }
      
      if (this.is_accountnum) return 6;
      if (this.is_serialnum) return 7;
      if (this.is_phone) return 10;
      if (this.is_zip) return 5;
      return 60;
    }
  },
};
</script>

<style scoped>
@media (max-width: 1000px) {
  .eye-icon {
    right: 1.3rem !important;
  }
  
  .eye-icon img {
    width: 2rem !important;
  }
  
}
</style>